import { useEffect, useState } from "react";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import {
  createCustomerApi,
  deleteCustomerApi,
  getCustomerByIdApi,
  getCustomersApi,
  updateCustomerApi,
  getCustomerAddressesApi,
} from "../../../services/operation/customer/customerService";

export const useCustomerViewModel = () => {
  const tenantId = getTenant();

  const [customers, setCustomers] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");

  const [totalCount, setTotalCount] = useState(0);

  // ================= LOAD =================
  const loadCustomers = async () => {
    try {
      setLoading(true);

      const res = await getCustomersApi(page, 10, search, tenantId!);

      setCustomers(res?.data?.data || []);

      setTotalCount(res?.data?.totalCount || 0);
    } catch {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [page, search]);

  // ================= GET ONE =================
  const getCustomerById = async (id: string) => {
    try {
      const res = await getCustomerByIdApi(id, tenantId!);

      return res?.data;
    } catch {
      toast.error("Failed to load customer");
    }
  };

  // ================= CREATE =================
  const createCustomer = async (data: any) => {
    try {
      const payload = {
        name: data.name,

        email: data.email,

        addresses: data.addresses,

        customerPhoneNumbers: data.customerPhoneNumbers,
      };

      const res = await createCustomerApi(payload, tenantId!);

      if (res?.success) {
        toast.success("Customer created");

        loadCustomers();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");

      return false;
    }
  };

  // ================= UPDATE =================
  const updateCustomer = async (data: any) => {
    try {
      const payload = {
        id: data.id,

        name: data.name,

        email: data.email,

        addresses: data.addresses,

        customerPhoneNumbers: data.customerPhoneNumbers,
      };

      const res = await updateCustomerApi(payload, tenantId!);

      if (res?.success) {
        toast.success("Customer updated");

        loadCustomers();

        return true;
      }

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");

      return false;
    }
  };

  // ================= DELETE =================
  const deleteCustomer = async (id: string) => {
    try {
      const res = await deleteCustomerApi(id, tenantId!);

      if (res?.success) {
        loadCustomers();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const loadCustomerOptions = async (
    search: string,
    _loadedOptions: readonly any[],
    additional?: { page: number },
  ) => {
    const page = additional?.page ?? 1;

    const result = await getCustomersApi(page, 20, search, tenantId!);

    const customers = result?.data?.data || [];

    const options = customers.map((x: any) => ({
      value: x.id,
      label: x.name,
      data: x,
      isNew: false,
    }));

    if (
      search &&
      !customers.some((x: any) => x.name.toLowerCase() === search.toLowerCase())
    ) {
      options.unshift({
        value: null,
        label: `+ Add "${search}" as new customer`,
        customerName: search,
        isNew: true,
      });
    }

    return {
      options,
      hasMore: customers.length === 20,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadAddressOptions = async (
    customerId: string,
    search: string,
    additional?: { page: number },
  ) => {
    const page = additional?.page ?? 1;

    const res = await getCustomerAddressesApi(
      customerId,
      page,
      20,
      search,
      tenantId!,
    );

    const addresses = res?.data?.data || res?.data || [];

    return {
      options: addresses.map((x: any) => ({
        value: x.id,

        label: [x.addressLine1, x.addressLine2, x.city, x.postalCode]
          .filter(Boolean)
          .join(", "),

        data: x,
      })),

      hasMore: addresses.length === 20,

      additional: {
        page: page + 1,
      },
    };
  };

  return {
    customers,
    loading,

    page,
    setPage,

    search,
    setSearch,

    totalCount,

    loadCustomers,

    getCustomerById,

    createCustomer,
    updateCustomer,
    deleteCustomer,
    loadCustomerOptions,
    loadAddressOptions,
  };
};
